// Dev-only visibility into Supabase requests that fail.
//
// Supabase calls are cross-origin, so the in-app browser's network tool never
// lists them: a failing one surfaces only as Chrome's bare
// "Failed to load resource: the server responded with a status of 400 ()",
// with no URL and no body. QA on 2026-09-12 hit exactly that on every full
// page load and nobody could say which request it was. This wraps the fetch
// the Supabase client uses and, in dev only, warns with the method, the path
// (query string kept — it names the table, filters, or RPC), the status, and
// the response body, which for PostgREST carries the error code and message.
//
// It never alters the response: the body is read from a clone, and any failure
// inside the diagnostic is swallowed so it can't turn a working call into a
// broken one.
const MAX_BODY_CHARS = 600;

const requestUrl = (input) => {
  if (typeof input === 'string') return input;
  if (input && typeof input.url === 'string') return input.url;
  return String(input ?? '');
};

const requestMethod = (input, init) =>
  String(init?.method || (input && typeof input === 'object' && input.method) || 'GET').toUpperCase();

// Origin dropped; the path and query are what identify the call.
const describeUrl = (url) => {
  try {
    const parsed = new URL(url);
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
};

/**
 * Returns a fetch that behaves exactly like `baseFetch` but reports any
 * response with a status >= 400 through `warn`. `enabled: false` returns
 * `baseFetch` untouched so production pays nothing.
 */
export function createDiagnosticFetch(baseFetch, { enabled = true, warn = console.warn } = {}) {
  if (!enabled || typeof baseFetch !== 'function') return baseFetch;

  return async function diagnosticFetch(input, init) {
    const response = await baseFetch(input, init);
    if (response && response.status >= 400) {
      // Detached from the caller: reading the clone must never delay or
      // break the real consumer of the response.
      Promise.resolve()
        .then(async () => {
          let body = '';
          try {
            body = (await response.clone().text()).slice(0, MAX_BODY_CHARS);
          } catch {
            body = '(body unreadable)';
          }
          warn(
            `[supabase] ${response.status} ${requestMethod(input, init)} ${describeUrl(requestUrl(input))}`,
            body,
          );
        })
        .catch(() => {});
    }
    return response;
  };
}

export default createDiagnosticFetch;
